// src/services/authService.js
import TokenService from './tokenService';
import { parseJwt } from '../utils/jwtUtils';
import { ROLES } from '../api/constants';
import { normalizePermission } from '../utils/permissions';
import { tenantApi } from './tenantApi';

// Store reference will be injected
let store = null;

class AuthService {
  // Initialize with Redux store
  static setStore(storeInstance) {
    store = storeInstance;
  }

  static async login(email) {
    try {
      if (!store) {
        throw new Error('Store not initialized in AuthService');
      }

      // Use RTK Query mutation via store dispatch
      const result = await store.dispatch(
        tenantApi.endpoints.loginGetCode.initiate(email)
      ).unwrap();
      
      // Save email in token service
      TokenService.setStoredEmail(email);
      
      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Unified Authentication with JWT Claims
   * Simplified approach using JWT as the primary source of truth
   */
  static async verifyCode(email, code) {
    try {
      // Auth verification started
      
      if (!store) {
        throw new Error('Store not initialized in AuthService');
      }

      // Use RTK Query mutation via store dispatch
      const tokenResponse = await store.dispatch(
        tenantApi.endpoints.verifyAndGetToken.initiate({ email, code })
      ).unwrap();

      // Bearer tokens: backend returns access and refresh tokens
      const { token, refreshToken, refreshTokenExpiryTime } = tokenResponse || {};
      const claims = token ? parseJwt(token) : null;
      
      if (!claims && token) {
        throw new Error('Invalid token format');
      }
      
      // **RBAC FIX: Handle role array from JWT**
      const roleClaimRaw = claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
            
      // Role claim can be either string or array
      const roleClaims = Array.isArray(roleClaimRaw) ? roleClaimRaw : [roleClaimRaw];
            

      // Priority: Admin > TenantAdmin > User
      let userRole = ROLES.USER; // default
      
      if (roleClaims.includes('Administrator')) {
        userRole = ROLES.ADMIN;
              } else if (roleClaims.includes('TenantAdmin')) {
        userRole = ROLES.TENANT_ADMIN;
              } else if (roleClaims.includes('User')) {
        userRole = ROLES.USER;
              }
      
            const userId = claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
      const tenantId = claims.TenantId;

      // **HYBRID MODEL: JWT Claims + API Validation**
      // Normalize permissions from JWT for consistent format
      const rawPermissions = claims.Permissions || [];
      const normalizedPermissions = rawPermissions.map(permission => {
        const normalized = normalizePermission(permission);
        return normalized;
      });

      let baseUserData = {
        email: claims.Email,
        role: userRole,
        userId: userId,
        tenantId: tenantId,
        permissions: normalizedPermissions, // Normalized permissions
        rawPermissions: rawPermissions, // Keep original for debugging
        lastTokenRefresh: Date.now()
      };

      
      // **STEP 1: Admin - Full Access (No Tenant Restrictions)**
      if (userRole === ROLES.ADMIN) {
                
        const adminValidationResult = {
          hasAccess: true,
          reason: 'admin_full_access',
          permissions: baseUserData.permissions || [],
          tenantModules: [], // Admin doesn't need tenant modules
          sources: {
            jwt: baseUserData.permissions || [],
            api: [],
            tenant: []
          }
        };

        const enhancedUserData = {
          ...baseUserData,
          validationResult: adminValidationResult
        };
        
        TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: enhancedUserData }, { isLogin: true });

        return {
          verified: true,
          token,
          refreshToken,
          user: enhancedUserData,
          authMethod: 'jwt_claims_admin'
        };
      }

      // **STEP 2: TenantAdmin and User - Extract Tenant Modules from JWT**
      if (userRole === ROLES.TENANT_ADMIN || userRole === ROLES.USER) {
        
        // Extract modules directly from JWT claims
        const tenantModules = claims.Modules || [];

        // Enhanced user data with tenant modules from JWT
        const enhancedBaseUserData = {
          ...baseUserData,
          tenantModules
        };

        if (userRole === ROLES.TENANT_ADMIN) {
          
          const tenantAdminValidationResult = {
            hasAccess: true,
            reason: 'tenant_admin_access',
            permissions: baseUserData.permissions || [],
            tenantModules,
            sources: {
              jwt: baseUserData.permissions || [],
              api: [],
              tenant: tenantModules
            }
          };

          const enhancedUserData = {
            ...enhancedBaseUserData,
            validationResult: tenantAdminValidationResult
          };
          
          TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: enhancedUserData }, { isLogin: true });

          return {
            verified: true,
            token,
            refreshToken,
            user: enhancedUserData,
            authMethod: 'jwt_claims_tenant_admin'
          };
        }

        // Continue with User role logic below with enhanced data
        baseUserData = enhancedBaseUserData;
      }

      // **STEP 3: User Role - Validation with Tenant Modules**
      if (userRole === ROLES.USER) {
        
        // Simple validation: User needs at least one permission to access system
        const userPermissions = baseUserData.permissions || [];
        const tenantModules = baseUserData.tenantModules || [];
        
        if (userPermissions.length === 0) {
          
          const deniedResult = {
            hasAccess: false,
            reason: 'no_permissions',
            permissions: [],
            tenantModules
          };

          const deniedUserData = {
            ...baseUserData,
            validationResult: deniedResult
          };

          TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: deniedUserData }, { isLogin: true });

          return {
            verified: true,
            token,
            refreshToken,
            user: deniedUserData,
            accessDenied: true,
            reason: 'no_permissions',
            authMethod: 'jwt_tenant_modules'
          };
        }

        // User has permissions - grant access
        
        const validationResult = {
          hasAccess: true,
          reason: 'permissions_granted',
          permissions: userPermissions,
          tenantModules,
          sources: {
            jwt: userPermissions,
            api: [],
            tenant: tenantModules
          }
        };

        const enhancedUserData = {
          ...baseUserData,
          validationResult
        };

        TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: enhancedUserData }, { isLogin: true });

        return {
          verified: true,
          token,
          refreshToken,
          user: enhancedUserData,
          authMethod: 'jwt_tenant_modules'
        };
      }

      // **STEP 3: Unknown Role - Deny Access**
      throw new Error('Geçersiz kullanıcı rolü');

    } catch (error) {
      console.error('Verification error:', error);
      throw error;
    }
  }



  static async refreshToken() {
    try {
      const currentRefreshToken = TokenService.getRefreshToken();
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }
      
      if (!store) {
        throw new Error('Store not initialized in AuthService');
      }

      // Use RTK Query mutation via store dispatch
      const tokenResponse = await store.dispatch(
        tenantApi.endpoints.refreshAuthToken.initiate(currentRefreshToken)
      ).unwrap();
      
      const { token, refreshToken, refreshTokenExpiryTime } = tokenResponse;
      const claims = parseJwt(token);
      if (!claims) throw new Error('Invalid token format');
      
      // Handle role array from JWT
      const roleClaimRaw = claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      const roleClaims = Array.isArray(roleClaimRaw) ? roleClaimRaw : [roleClaimRaw];
      
      let userRole = ROLES.USER; // default
      if (roleClaims.includes('Administrator')) {
        userRole = ROLES.ADMIN;
      } else if (roleClaims.includes('TenantAdmin')) {
        userRole = ROLES.TENANT_ADMIN;
      } else if (roleClaims.includes('User')) {
        userRole = ROLES.USER;
      }
      
      // Extract tenant modules from JWT
      const tenantModules = claims.Modules || [];
      
      // Normalize permissions
      const rawPermissions = claims.Permissions || [];
      const normalizedPermissions = rawPermissions.map(permission => {
        return normalizePermission(permission);
      });
      
      const userData = {
        email: claims.Email,
        role: userRole,
        userId: claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
        tenantId: claims.TenantId,
        permissions: normalizedPermissions,
        rawPermissions: rawPermissions,
        tenantModules: tenantModules, // Add tenant modules from JWT
        lastTokenRefresh: Date.now()
      };
      
      TokenService.setTokens({ accessToken: token, refreshToken, refreshTokenExpiryTime, user: userData }, { isLogin: false });
      return { token, refreshToken, user: userData };
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  static logout() {
    // Broadcast logout to other tabs - this is the ONLY place that should broadcast
    TokenService.clearTokens({ broadcast: true });
    TokenService.setStoredEmail(null);
    return true;
  }

  static isAuthenticated() {
    return TokenService.isAuthenticated();
  }

  static getAuthHeader() {
    const token = TokenService.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  static getCurrentUser() {
    return TokenService.getUser();
  }
  
  // Check if the current session is valid and user is authenticated
  static validateSession() {
    const token = TokenService.getAccessToken();
    if (!token) return false;
    
    // Check if token is expired
    if (TokenService.isTokenExpired(token)) {
      // Try to use refresh token if available
      const refreshToken = TokenService.getRefreshToken();
      if (!refreshToken) {
        this.logout();
        return false;
      }
      
      // Token refresh will be handled by an interceptor
      // Here we just return false to indicate the current session is invalid
      return false;
    }
    
    return true;
  }
}

export default AuthService;